import React from "react";
import { Status, SupportedIntegrations } from "@repo/database";
import { capitalize } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  DisableGoogleReserveIntegration,
  EnableGoogleReserveIntegration,
} from "~/components/integrations/google-reserve";
import IntegrationCard from "~/components/integrations/integration-card";
import {
  DisableMollieIntegration,
  EnableMollieIntegration,
} from "~/components/integrations/mollie";
import {
  DisableVoysIntegration,
  EnableVoysIntegration,
} from "~/components/integrations/voys";
import { SettingsHeader } from "~/components/layout/settings-header";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/components/ui/dialog";
import { useIntegration } from "~/hooks/use-integration";
import { api } from "~/lib/api";

export const Route = createFileRoute("/_main/settings/_layout/integrations/")({
  component: IntegrationsPage,
});

function IntegrationsPage() {
  const { t } = useTranslation("common");
  const utils = api.useUtils();
  const { integrations, userIntegrations } = useIntegration();

  const integrationDetails: Record<
    string,
    {
      title: string;
      iconUrl?: string;
      description: string;
      content: React.ReactNode;
      disableContent: React.ReactNode;
      showFooter?: boolean;
    }
  > = {
    [SupportedIntegrations.mollie]: {
      title: t("Integrations.Mollie.title"),
      iconUrl: "/images/integrations/mollie.png",
      description: t("Integrations.Mollie.short_description"),
      content: <EnableMollieIntegration />,
      disableContent: <DisableMollieIntegration />,
      showFooter: false,
    },
    [SupportedIntegrations.google_reserve]: {
      title: t("Integrations.GoogleReserve.title"),
      iconUrl: "/images/integrations/google-icon.png",
      description: t("Integrations.GoogleReserve.short_description"),
      content: <EnableGoogleReserveIntegration />,
      disableContent: <DisableGoogleReserveIntegration />,
    },
    [SupportedIntegrations.voys]: {
      title: t("Integrations.Voys.title"),
      iconUrl: "/images/integrations/voys-icon.png",
      description: t("Integrations.Voys.short_description"),
      content: <EnableVoysIntegration />,
      disableContent: <DisableVoysIntegration />,
    },
    [SupportedIntegrations.social_deal]: {
      title: t("Integrations.SocialDeal.title"),
      iconUrl: "/images/integrations/social-deal-icon.png",
      description: t("Integrations.SocialDeal.short_description"),
      content: <EnableVoysIntegration />,
      disableContent: <DisableVoysIntegration />,
    },
    [SupportedIntegrations.thuisbezorgd]: {
      title: t("Integrations.Thuisbezorgd.title"),
      iconUrl: "/images/integrations/thuisbezorgd-icon.png",
      description: t("Integrations.Thuisbezorgd.short_description"),
      content: <EnableVoysIntegration />,
      disableContent: <DisableVoysIntegration />,
    },
    [SupportedIntegrations.uber_eats]: {
      title: t("Integrations.UberEats.title"),
      iconUrl: "/images/integrations/uber-eats-icon.png",
      description: t("Integrations.UberEats.short_description"),
      content: <EnableVoysIntegration />,
      disableContent: <DisableVoysIntegration />,
    },
  };

  const { mutate: enableIntegration } =
    api.integration.enableIntegration.useMutation({
      async onSuccess(_, id) {
        const integration = integrations.find(
          (integration) => integration.id === id,
        );

        let title = t("Integrations.integration");

        if (integration) {
          const details = integrationDetails[integration.type];
          title = details.title;
        }

        toast.success(
          capitalize(
            t("Integrations.enable_success", {
              integration: title,
            }),
          ),
        );
        await utils.integration.invalidate();
      },
    });

  const { mutate: disableIntegration } =
    api.integration.disableIntegration.useMutation({
      async onSuccess(_, id) {
        const integration = integrations.find(
          (integration) => integration.id === id,
        );
        let title = t("Integrations.integration");

        if (integration) {
          const details = integrationDetails[integration.type];
          title = details.title;
        }

        toast.success(
          capitalize(
            t("Integrations.disable_success", {
              integration: title,
            }),
          ),
        );
        await utils.integration.invalidate();
      },
    });

  const handleEnableIntegration = (integrationId: string) => {
    enableIntegration(integrationId);
  };

  const handleDisableIntegration = (integrationId: string) => {
    disableIntegration(integrationId);
  };
  return (
    <div className="flex flex-col gap-4 @container">
      <SettingsHeader title={t("Integrations.page_title")} />
      <div className="grid @3xl:grid-cols-3 @xl:grid-cols-2 grid-cols-1 gap-6 @container">
        {integrations.map((integration) => {
          const details = integrationDetails[integration.type];

          if (!details) return null;

          const {
            title,
            description,
            content,
            showFooter,
            disableContent,
            iconUrl,
          } = details;

          const userIntegration = userIntegrations.find(
            (userIntegration) =>
              userIntegration.integrationId === integration.id,
          );

          return (
            <IntegrationCard
              key={integration.id}
              title={title}
              description={description}
              comingSoon={integration.comingSoon}
              icon={
                <img
                  src={iconUrl}
                  alt={title}
                  className="w-full h-[50px] object-contain"
                />
              }
            >
              {integration.comingSoon && (
                <Button disabled variant="brand">
                  {t("coming_soon")}
                </Button>
              )}
              {(!userIntegration ||
                userIntegration.status === Status.inactive) &&
                !integration.comingSoon && (
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="brand">{t("enable")}</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl w-full">
                      <DialogHeader>
                        <DialogTitle>{title}</DialogTitle>
                      </DialogHeader>
                      <div className="overflow-auto p-4">{content}</div>
                      {showFooter !== false && (
                        <DialogFooter>
                          <DialogClose asChild>
                            <Button>{t("cancel")}</Button>
                          </DialogClose>
                          <DialogClose asChild>
                            <Button
                              onClick={() => {
                                handleEnableIntegration(integration.id);
                              }}
                              variant="success"
                            >
                              {t("enable")}
                            </Button>
                          </DialogClose>
                        </DialogFooter>
                      )}
                    </DialogContent>
                  </Dialog>
                )}

              {userIntegration?.status === Status.active && (
                <Dialog>
                  <div className="flex flex-col gap-2">
                    <span className="text-center text-sm text-muted-foreground">
                      {t("Integrations.enabled_since", {
                        date: format(userIntegration.createdAt, "P"),
                      })}
                    </span>

                    <DialogTrigger asChild>
                      <Button className="w-full" variant="destructive">
                        {t("disable")}
                      </Button>
                    </DialogTrigger>
                  </div>

                  <DialogContent className="max-w-2xl w-full">
                    <DialogHeader>
                      <DialogTitle>{title}</DialogTitle>
                    </DialogHeader>
                    <div className="overflow-auto p-4">{disableContent}</div>
                    {showFooter !== false && (
                      <DialogFooter>
                        <DialogClose asChild>
                          <Button>{t("cancel")}</Button>
                        </DialogClose>
                        <DialogClose asChild>
                          <Button
                            onClick={() => {
                              handleDisableIntegration(integration.id);
                            }}
                            variant="destructive"
                          >
                            {t("disable")}
                          </Button>
                        </DialogClose>
                      </DialogFooter>
                    )}
                  </DialogContent>
                </Dialog>
              )}
            </IntegrationCard>
          );
        })}
      </div>
    </div>
  );
}
